import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Search, Wrench, Droplets, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import logoSolyra from '@/assets/logo-solyra.webp';
import logoSolyraWhite from '@/assets/logo-solyra-white.webp';
import { prefetchContact, prefetchServicii } from '@/lib/prefetch';

const OfferRequestForm = lazy(() => import('@/components/OfferRequestForm'));

const serviceItems = [
  {
    label: 'Diagnosticare sistem',
    description: 'Verificare și depanare sistem',
    icon: Search,
    anchorId: 'diagnosticare',
  },
  {
    label: 'Curățare profesională',
    description: 'Optimizare panouri solare',
    icon: Droplets,
    anchorId: 'curatare',
  },
  {
    label: 'Reparații și înlocuiri',
    description: 'Invertor, panouri, baterie',
    icon: Wrench,
    anchorId: 'reparatii',
  },
  {
    label: 'Extindere sistem',
    description: 'Adaugi panouri, baterii sau invertor',
    icon: ArrowUpDown,
    anchorId: 'extindere',
  },
];

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    if (location.pathname === href) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleServiceClick = (anchorId: string) => {
    setIsServicesOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);

    const scrollToAnchor = () => {
      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    };

    if (location.pathname === '/servicii') {
      scrollToAnchor();
    } else {
      navigate('/servicii');
      setTimeout(scrollToAnchor, 300);
    }
  };
  const scrollToAbonamente = () => {
    document.getElementById('planuri')?.scrollIntoView({ behavior: 'smooth' });
  };
  // Pages with hero images where navbar should be transparent initially
  const heroPages = ['/', '/servicii', '/analiza-gratuita', '/solyra-protect', '/solyra-protect-business', '/solyra-protect-parteneri'];
  const hasHero = heroPages.includes(location.pathname);
  const isTransparent = hasHero && !isScrolled;
  const isProtectPage = location.pathname === '/solyra-protect';

  const navbarClass = `header fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isTransparent ? '' : 'is-scrolled'}`;

  return (
    <header className={navbarClass}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" onClick={() => handleNavClick('/')} className="brand relative block" style={{ width: 124, height: 63 }}>
            <img
              src={logoSolyraWhite}
              alt="Solyra"
              aria-hidden={!isTransparent}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width="124"
              height="63"
              className="brand-logo absolute inset-0 transition-opacity duration-200"
              style={{ opacity: isTransparent ? 1 : 0 }}
            />
            <img
              src={logoSolyra}
              alt="Solyra"
              aria-hidden={isTransparent}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              width="124"
              height="63"
              className="brand-logo absolute inset-0 transition-opacity duration-200"
              style={{ opacity: isTransparent ? 0 : 1 }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav hidden lg:flex items-center gap-8">
            <Link to="/solyra-protect" onClick={() => handleNavClick('/solyra-protect')} className="text-sm font-medium hover:opacity-80 transition-opacity">
              Solyra Protect
            </Link>

            {/* Services Dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="text-sm font-medium hover:opacity-80 transition-opacity flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer font-[inherit] text-[inherit]"
              >
                Servicii
                <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[420px] bg-background border border-border rounded-2xl shadow-xl p-5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex gap-5">
                    {/* Left info */}
                    <div className="w-[140px] flex-shrink-0 bg-muted/50 rounded-xl p-4 flex flex-col justify-between">
                      <div>
                        <p className="text-xs font-semibold text-accent uppercase tracking-wider mb-1">Servicii profesionale</p>
                        <p className="text-base font-bold text-foreground leading-tight">Reparare & Protecție</p>
                        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">Tehnicieni experți · Intervenție rapidă</p>
                      </div>
                      <Link
                        to="/servicii"
                        onClick={() => { setIsServicesOpen(false); handleNavClick('/servicii'); }}
                        className="text-xs font-semibold text-foreground hover:text-accent transition-colors mt-4 flex items-center gap-1"
                      >
                        Vezi toate →
                      </Link>
                    </div>

                    {/* Right service list */}
                    <div className="flex-1 flex flex-col gap-1">
                      {serviceItems.map((item) => (
                        <button
                          key={item.anchorId}
                          onClick={() => handleServiceClick(item.anchorId)}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 transition-colors text-left w-full group"
                        >
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10 transition-colors">
                            <item.icon className="w-5 h-5 text-muted-foreground group-hover:text-accent transition-colors" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{item.label}</p>
                            <p className="text-xs text-muted-foreground">{item.description}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" onClick={() => handleNavClick('/contact')} onMouseEnter={prefetchContact} onFocus={prefetchContact} onTouchStart={prefetchContact} className="text-sm font-medium hover:opacity-80 transition-opacity">
              Contact
            </Link>
            {isProtectPage ? (
              <Button
                onClick={scrollToAbonamente}
                className="btn-primary bg-accent hover:bg-accent/90 text-accent-foreground h-10 px-4 rounded-full"
              >
                Vezi abonamentele
              </Button>
            ) : (
              <Button
                onClick={() => setIsFormOpen(true)}
                className="btn-primary bg-accent hover:bg-accent/90 text-accent-foreground h-10 px-4 rounded-full"
              >
                Solicită ofertă
              </Button>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-foreground" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 mobile-menu z-40">
          <nav className="nav mobile-nav flex flex-col items-center gap-6 p-8">
            <Link to="/solyra-protect" onClick={() => { handleNavClick('/solyra-protect'); setIsMobileMenuOpen(false); }} className="text-2xl font-medium hover:opacity-80 transition-opacity">
              Solyra Protect
            </Link>

            {/* Mobile Services Accordion */}
            <div className="flex flex-col items-center w-full max-w-xs">
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className="text-2xl font-medium hover:opacity-80 transition-opacity flex items-center gap-2"
              >
                Servicii
                <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileServicesOpen && (
                <div className="mt-3 flex flex-col gap-2 w-full">
                  {serviceItems.map((item) => (
                    <button
                      key={item.anchorId}
                      onClick={() => handleServiceClick(item.anchorId)}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/60 transition-colors text-left w-full"
                    >
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-foreground">{item.label}</p>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                      </div>
                    </button>
                  ))}
                  <Link
                    to="/servicii"
                    onClick={() => { setIsMobileMenuOpen(false); handleNavClick('/servicii'); }}
                    className="text-sm font-semibold text-accent text-center mt-1"
                  >
                    Vezi toate serviciile →
                  </Link>
                </div>
              )}
            </div>

            <Link to="/contact" onClick={() => { handleNavClick('/contact'); setIsMobileMenuOpen(false); }} onTouchStart={prefetchContact} className="text-2xl font-medium hover:opacity-80 transition-opacity">
              Contact
            </Link>
            {isProtectPage ? (
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  scrollToAbonamente();
                }}
                className="btn-primary bg-accent hover:bg-accent/90 text-accent-foreground mt-4 w-full max-w-xs"
              >
                Vezi abonamentele
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsFormOpen(true);
                }}
                className="btn-primary bg-accent hover:bg-accent/90 text-accent-foreground mt-4 w-full max-w-xs"
              >
                Solicită ofertă
              </Button>
            )}
          </nav>
        </div>
      )}

      {isFormOpen && (
        <Suspense fallback={null}>
          <OfferRequestForm open={isFormOpen} onOpenChange={setIsFormOpen} />
        </Suspense>
      )}
    </header>
  );
};

export default Navbar;
